(() => {
  if (customElements.get('dl-page-visual-roll-difference')) return;

  class DlPageVisualRollDifference extends HTMLElement {
    connectedCallback() {
      this.hero = this.querySelector('[data-hero]');
      this.heading = this.querySelector('[data-hero-heading]');
      this.headingAccent = this.querySelector('[data-heading-accent]');
      this.cursor = this.querySelector('[data-hero-cursor]');
      this.cards = Array.from(this.querySelectorAll('[data-hero-card]'));
      this.gallery = this.querySelector('[data-gallery]');
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.mobileActiveIndex = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.heroProgress = this.reducedMotion ? 1 : 0;
      this.heroAnimationStarted = false;
      this.mobileTouchStart = null;
      this.notifyPopupOpen = false;
      this.abortController = new AbortController();
      this.raf = 0;
      this.heroRaf = 0;
      this.cursorRaf = 0;
      this.cursorTarget = { x: -100, y: -100 };
      this.cursorPosition = { x: -100, y: -100 };
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this);
      this.header = this.resolveHeader();
      this.pageStickyHeader = null;
      if (this.header) this.resizeObserver.observe(this.header);
      this.syncPageStickyHeader();
      this.bindEvents();
      this.setupVideoAutoplay();
      this.setMobileSlide(0, false);
      this.measure();
      document.fonts?.ready?.then(() => {
        if (this.isConnected) this.syncMobileQaHeight(this.mobileActiveIndex);
      });
      this.renderHero(this.heroProgress, this.getHeroColorProgress(this.heroProgress));
      this.render();
      this.classList.add('is-hero-initialized');
      this.setupHeroIntro();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.pageStickyHeaderObserver?.disconnect();
      this.heroObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.notifyPopupObserver?.disconnect();
      cancelAnimationFrame(this.raf);
      cancelAnimationFrame(this.heroRaf);
      cancelAnimationFrame(this.cursorRaf);
      window.clearTimeout(this.mobileTransitionTimer);
      window.clearTimeout(this.notifyPopupFallbackTimer);
      document.documentElement.classList.remove('dl-vrd-notify-popup-open');
    }

    bindEvents() {
      const signal = this.abortController.signal;
      window.addEventListener('scroll', () => this.requestRender(), { passive: true, signal });
      this.gallery?.addEventListener('wheel', (event) => this.handleGalleryWheel(event), { passive: false, signal });
      this.gallery?.addEventListener('touchstart', (event) => this.handleMobileTouchStart(event), { passive: true, signal });
      this.gallery?.addEventListener('touchend', (event) => this.handleMobileTouchEnd(event), { passive: true, signal });
      this.gallery?.addEventListener('touchcancel', () => { this.mobileTouchStart = null; }, { passive: true, signal });
      this.hero?.addEventListener('pointerenter', (event) => this.showCursor(event), { signal });
      this.hero?.addEventListener('pointermove', (event) => this.moveCursor(event), { passive: true, signal });
      this.hero?.addEventListener('pointerleave', () => this.hideCursor(), { signal });
      this.cards.forEach((card, index) => card.addEventListener('click', () => {
        this.trackHeroCardClick(card, index);
        this.goToSlide(Number(card.dataset.slideIndex));
      }, { signal }));
      this.querySelectorAll('[data-hero-explore]').forEach((button) => button.addEventListener('click', () => {
        this.goToSlide(0);
      }, { signal }));
      this.querySelectorAll('[data-mobile-prev]').forEach((button) => button.addEventListener('click', () => {
        this.setMobileSlide(this.mobileActiveIndex - 1);
      }, { signal }));
      this.querySelectorAll('[data-mobile-next]').forEach((button) => button.addEventListener('click', () => {
        this.setMobileSlide(this.mobileActiveIndex + 1);
      }, { signal }));
      this.querySelectorAll('.dl-vrd__media video').forEach((video) => {
        const media = video.closest('.dl-vrd__media');
        video.loop = false;
        video.addEventListener('play', () => media?.classList.add('is-playing'), { signal });
        video.addEventListener('ended', () => {
          video.autoplay = false;
          video.controls = false;
          media?.classList.remove('is-playing');
          try { video.currentTime = 0; } catch (_) {}
        }, { signal });
        if (!video.paused && !video.ended) media?.classList.add('is-playing');
      });
      this.querySelectorAll('[data-play]').forEach((button) => button.addEventListener('click', () => {
        const media = button.closest('.dl-vrd__media');
        const video = media?.querySelector('video');
        if (!video) return;
        video.dataset.vrdAutoplayDone = 'true';
        this.videoObserver?.unobserve(media);
        video.autoplay = false;
        video.loop = false;
        this.prepareVideo(video);
        video.controls = true;
        video.play().catch(() => media.classList.remove('is-playing'));
      }, { signal }));
      this.querySelectorAll('[data-copy-code]').forEach((button) => button.addEventListener('click', () => {
        this.copyDiscountCode(button);
      }, { signal }));
      this.querySelectorAll('[data-vrd-notify-me]').forEach((button) => button.addEventListener('click', () => {
        this.showNotifyMePopup(button.dataset.notifyPopId || '');
      }, { signal }));
      document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target?.closest?.('[data-slide]');
        if (slide && this.contains(slide)) this.goToSlide(Number(slide.dataset.slide), false);
      }, { signal });
      const syncPageStickyHeader = () => {
        this.syncPageStickyHeader();
        this.measure();
      };
      document.addEventListener('shopify:section:load', syncPageStickyHeader, { signal });
      document.addEventListener('shopify:section:unload', syncPageStickyHeader, { signal });
      document.addEventListener('shopify:section:reorder', syncPageStickyHeader, { signal });
    }

    setupVideoAutoplay() {
      const autoplayVideos = Array.from(this.querySelectorAll('video[data-vrd-autoplay="true"]'));
      if (!autoplayVideos.length || this.reducedMotion) return;

      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const media = entry.target;
          const slide = media.closest('[data-slide]');
          if (window.innerWidth < 991 && !slide?.classList.contains('is-mobile-active')) return;
          const video = media.querySelector('video[data-vrd-autoplay="true"]');
          if (!video || video.dataset.vrdAutoplayDone === 'true') return;
          video.dataset.vrdAutoplayDone = 'true';
          video.controls = false;
          this.videoObserver.unobserve(media);
          this.prepareVideo(video);
          video.play().catch(() => media.classList.remove('is-playing'));
        });
      }, { rootMargin: '160px 0px', threshold: 0.15 });

      autoplayVideos.forEach((video) => this.videoObserver.observe(video.closest('.dl-vrd__media')));
    }

    prepareVideo(video) {
      if (!video || video.dataset.vrdSourceReady === 'true') return;
      let hasSource = false;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        hasSource = true;
      });
      video.dataset.vrdSourceReady = 'true';
      if (hasSource) video.load();
    }

    handleGalleryWheel(event) {
      if (window.innerWidth < 991 || !this.gallery || !this.track) return;

      const horizontalDelta = this.normalizeWheelDelta(event.deltaX, event.deltaMode);
      const verticalDelta = this.normalizeWheelDelta(event.deltaY, event.deltaMode);
      if (Math.abs(horizontalDelta) < 1 || Math.abs(horizontalDelta) <= Math.abs(verticalDelta)) return;

      const galleryTop = this.gallery.getBoundingClientRect().top + window.scrollY;
      const scrollStart = galleryTop - (this.headerHeight || 0);
      const scrollEnd = scrollStart + (this.galleryScrollRange || Math.max(1, this.travel));
      const currentScroll = window.scrollY;
      const movingForward = horizontalDelta > 0;
      const canMove = movingForward ? currentScroll < scrollEnd - 1 : currentScroll > scrollStart + 1;
      if (!canMove) return;

      event.preventDefault();
      const nextScroll = Math.max(scrollStart, Math.min(scrollEnd, currentScroll + horizontalDelta));
      window.scrollTo({ top: nextScroll, behavior: 'auto' });
    }

    normalizeWheelDelta(delta, deltaMode) {
      if (deltaMode === WheelEvent.DOM_DELTA_LINE) return delta * 16;
      if (deltaMode === WheelEvent.DOM_DELTA_PAGE) return delta * window.innerHeight;
      return delta;
    }

    handleMobileTouchStart(event) {
      if (window.innerWidth >= 991 || event.touches.length !== 1) return;
      if (event.target.closest('a, button, input, select, textarea, video[controls]')) return;
      const touch = event.touches[0];
      this.mobileTouchStart = { x: touch.clientX, y: touch.clientY };
    }

    handleMobileTouchEnd(event) {
      if (!this.mobileTouchStart || window.innerWidth >= 991) return;
      const touch = event.changedTouches[0];
      const start = this.mobileTouchStart;
      this.mobileTouchStart = null;
      if (!touch) return;

      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.1) return;
      this.setMobileSlide(this.mobileActiveIndex + (deltaX < 0 ? 1 : -1));
    }

    setupHeroIntro() {
      if (!this.hero) return;
      if (this.reducedMotion) {
        this.renderHero(1, 1);
        this.classList.add('is-hero-intro-complete');
        return;
      }

      this.heroObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.heroObserver.disconnect();
        this.startHeroIntro();
      }, { threshold: 0.2 });
      this.heroObserver.observe(this.hero);
    }

    startHeroIntro() {
      if (this.heroAnimationStarted) return;
      this.heroAnimationStarted = true;
      const duration = window.innerWidth < 991 ? 700 : 1100;
      const startTime = performance.now();

      const animate = (time) => {
        const progress = Math.min(1, (time - startTime) / duration);
        this.heroProgress = progress;
        this.renderHero(progress, this.getHeroColorProgress(progress));
        if (progress < 1) {
          this.heroRaf = requestAnimationFrame(animate);
        } else {
          this.classList.add('is-hero-intro-complete');
        }
      };

      this.heroRaf = requestAnimationFrame(animate);
    }

    getHeroColorProgress(progress) {
      return Math.max(0, Math.min(1, (progress - .62) / .38));
    }

    showNotifyMePopup(popupId) {
      const id = popupId.trim();
      if (!id) return;
      const popup = document.getElementById(id);
      if (!popup || popup.tagName.toLowerCase() !== 'dl-pop-h') return;

      let isFinished = false;
      try {
        isFinished = Boolean(window.localStorage.getItem(`dl-pop-h-finished-${id}`));
      } catch (_) {
        // Storage may be unavailable; the popup can still be shown.
      }

      if (isFinished) {
        const nextPopupId = popup.btn?.dataset.dlNextPopId?.trim();
        if (nextPopupId) this.showNotifyMePopup(nextPopupId);
        return;
      }
      this.watchNotifyPopup(popup);
      // CTA-triggered popups should open immediately. This mirrors the site's
      // existing manual popup entry point and avoids leaving only the page lock
      // visible while an analytics SDK is still becoming ready.
      if (typeof popup._showPopup === 'function') {
        popup._showPopup(true);
      } else {
        popup.show?.(true);
      }
    }

    watchNotifyPopup(popup) {
      const overlay = popup.overlay || popup.querySelector('.dl-pop-h-overlay');
      if (!overlay) return;

      const gallerySticky = this.querySelector('.dl-vrd__gallery-sticky');
      if (gallerySticky && window.innerWidth >= 991) {
        const rect = gallerySticky.getBoundingClientRect();
        this.style.setProperty('--vrd-popup-gallery-top', `${rect.top}px`);
        this.style.setProperty('--vrd-popup-gallery-left', `${rect.left}px`);
        this.style.setProperty('--vrd-popup-gallery-width', `${rect.width}px`);
      }

      this.notifyPopupObserver?.disconnect();
      window.clearTimeout(this.notifyPopupFallbackTimer);

      const syncState = () => {
        const isOpen = overlay.classList.contains('dl-show');
        this.notifyPopupOpen = isOpen;
        document.documentElement.classList.toggle('dl-vrd-notify-popup-open', isOpen);
        if (!isOpen) this.requestRender();
      };

      // Apply before DLPopH locks the page so its fixed-body lock never causes
      // the sticky gallery behind the modal to lose its current visual state.
      this.notifyPopupOpen = true;
      document.documentElement.classList.add('dl-vrd-notify-popup-open');
      this.notifyPopupObserver = new MutationObserver(syncState);
      this.notifyPopupObserver.observe(overlay, { attributes: true, attributeFilter: ['class'] });
      this.notifyPopupFallbackTimer = window.setTimeout(() => {
        if (!overlay.classList.contains('dl-show')) syncState();
      }, 5000);
    }

    async trackHeroCardClick(card, index) {
      if (typeof window.DLPixelH?.capture !== 'function') return;

      const eventData = {
        question: card.dataset.question || '',
        answer: card.dataset.answer || '',
        itemIndex: String(index + 1),
        totalItems: String(this.cards.length)
      };
      const productHandle = card.dataset.productHandle;
      const variantId = card.dataset.variantId;

      if (productHandle && typeof window.DLPixelDataHelper?.ProductHelper?.getProductByHandle === 'function') {
        try {
          const productData = await window.DLPixelDataHelper.ProductHelper.getProductByHandle(productHandle, variantId || null);
          eventData.product = productData?.product;
          eventData.variant = productData?.variant;
        } catch (_) {
          // Product analytics must never interrupt the card navigation.
        }
      }

      window.DLPixelH.capture('wb_vrd_hero_card_click', eventData);
    }

    showCursor(event) {
      const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (!this.cursor || this.reducedMotion || !supportsHover) return;
      this.cursorTarget = { x: event.clientX, y: event.clientY };
      this.cursorPosition = { ...this.cursorTarget };
      this.cursor.classList.add('is-visible');
      this.moveCursor(event);
      this.animateCursor();
    }

    moveCursor(event) {
      if (!this.cursor) return;
      this.cursorTarget.x = event.clientX;
      this.cursorTarget.y = event.clientY;
      this.cursor.classList.toggle('is-media', Boolean(event.target.closest('[data-hero-card]')));

      if (event.target.closest('[data-hero-heading]') && this.heading) {
        const rect = this.heading.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - .5) * 2;
        const y = ((event.clientY - rect.top) / rect.height - .5) * 2;
        this.heading.style.setProperty('--vrd-tilt-x', `${-y * 7}deg`);
        this.heading.style.setProperty('--vrd-tilt-y', `${x * 9}deg`);
      } else {
        this.resetHeadingTilt();
      }
    }

    hideCursor() {
      this.cursor?.classList.remove('is-visible', 'is-media');
      this.resetHeadingTilt();
      cancelAnimationFrame(this.cursorRaf);
      this.cursorRaf = 0;
    }

    animateCursor() {
      if (!this.cursor || !this.cursor.classList.contains('is-visible')) return;
      this.cursorPosition.x += (this.cursorTarget.x - this.cursorPosition.x) * .1;
      this.cursorPosition.y += (this.cursorTarget.y - this.cursorPosition.y) * .1;
      this.cursor.style.transform = `translate3d(${this.cursorPosition.x}px, ${this.cursorPosition.y}px, 0) translate(-50%, -50%)`;
      this.cursorRaf = requestAnimationFrame(() => this.animateCursor());
    }

    resetHeadingTilt() {
      this.heading?.style.setProperty('--vrd-tilt-x', '0deg');
      this.heading?.style.setProperty('--vrd-tilt-y', '0deg');
    }

    measure() {
      if (!this.gallery || !this.track) return;
      const headerHeight = this.getHeaderHeight();
      const pageStickyHeaderHeight = this.getPageStickyHeaderHeight();
      const galleryHeight = Math.max(1, window.innerHeight - headerHeight);
      const galleryScale = Math.min(window.innerWidth / 1920, galleryHeight / 930);
      const desktopScale = window.innerWidth >= 991 ? window.innerWidth / 1920 : 1;
      const baseRadius = Number.parseFloat(getComputedStyle(this).getPropertyValue('--vrd-radius-base')) || 0;
      this.headerHeight = headerHeight;
      this.pageStickyHeaderHeight = pageStickyHeaderHeight;
      this.classList.toggle('has-page-sticky-header', pageStickyHeaderHeight > 0);
      this.style.setProperty('--vrd-radius', `${baseRadius * desktopScale}px`);
      this.style.setProperty('--vrd-radius-mobile', `${Math.max(10, baseRadius * .7)}px`);
      this.style.setProperty('--vrd-header-height', `${headerHeight}px`);
      this.style.setProperty('--vrd-page-sticky-header-height', `${pageStickyHeaderHeight}px`);
      this.style.setProperty('--vrd-gallery-height', `${galleryHeight}px`);
      this.style.setProperty('--vrd-gallery-title-height', `${142 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-title-padding-top', `${40 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-title-padding-bottom', `${24 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-horizontal-padding', `${36 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-title-font-size', `${56 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-track-height', `${788 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-slide-width', `${1806 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-media-left', `${36 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-media-top', `${36 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-media-width', `${1166 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-media-height', `${648 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-info-left', `${1202 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-info-width', `${603 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-info-padding', `${36 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-product-size', `${232 * galleryScale}px`);
      this.style.setProperty('--vrd-gallery-question-font-size', `${40 * galleryScale}px`);
      this.renderHero(this.heroProgress, this.getHeroColorProgress(this.heroProgress));
      if (window.innerWidth < 991) {
        this.travel = 0;
        this.galleryScrollRange = 1;
        this.gallery.style.height = '';
        this.track.style.transform = 'none';
        this.slides.forEach((slide) => {
          const active = slide.classList.contains('is-mobile-active');
          slide.setAttribute('aria-hidden', String(!active));
          slide.inert = !active;
        });
        this.syncMobileQaHeight(this.mobileActiveIndex);
        this.requestRender();
        return;
      }
      this.mobileQaHeight = 0;
      this.style.removeProperty('--vrd-mobile-qa-height');
      this.slides.forEach((slide) => {
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
      });
      const sticky = this.gallery.querySelector('.dl-vrd__gallery-sticky');
      this.travel = Math.max(0, this.track.scrollWidth - window.innerWidth);
      const stickyHeight = sticky?.getBoundingClientRect().height || galleryHeight;
      this.galleryScrollRange = Math.max(1, this.travel);
      this.gallery.style.height = `${stickyHeight + this.travel}px`;
      this.requestRender();
    }

    resolveHeader() {
      return document.querySelector('sticky-header')
        || document.querySelector('#shopify-section-header')
        || document.querySelector('.section-header')
        || document.querySelector('.shopify-section-group-header-group');
    }

    resolvePageStickyHeader() {
      return document.querySelector('dl-page-sticky-header-h.dl-page-sticky-header-h-section');
    }

    syncPageStickyHeader() {
      const nextHeader = this.resolvePageStickyHeader();
      if (nextHeader === this.pageStickyHeader) return;

      if (this.pageStickyHeader) this.resizeObserver?.unobserve(this.pageStickyHeader);
      this.pageStickyHeaderObserver?.disconnect();
      this.pageStickyHeaderObserver = null;
      this.pageStickyHeader = nextHeader;
      if (!nextHeader) return;

      this.resizeObserver?.observe(nextHeader);
      this.pageStickyHeaderObserver = new MutationObserver(() => this.measure());
      this.pageStickyHeaderObserver.observe(nextHeader, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    getPageStickyHeaderHeight() {
      this.syncPageStickyHeader();
      const header = this.pageStickyHeader;
      if (!header) return 0;
      const isVisible = header.classList.contains('dl-page-sticky-header-h-fixed')
        || header.classList.contains('dl-show');
      if (!isVisible) return 0;
      return Math.max(0, Math.round(header.getBoundingClientRect().height));
    }

    getHeaderHeight() {
      const pageStickyHeaderHeight = this.getPageStickyHeaderHeight();
      if (pageStickyHeaderHeight > 0) return pageStickyHeaderHeight;
      const header = this.header || this.resolveHeader();
      if (!header) return 0;
      const rect = header.getBoundingClientRect();
      return Math.max(0, Math.round(Math.max(rect.height, rect.bottom)));
    }

    getStickyHeaderHeight() {
      const pageStickyHeaderHeight = this.getPageStickyHeaderHeight();
      if (pageStickyHeaderHeight > 0) return pageStickyHeaderHeight;
      const header = this.header || this.resolveHeader();
      if (!header) return 0;
      return Math.max(0, Math.round(header.getBoundingClientRect().height));
    }

    requestRender() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = 0;
        this.render();
      });
    }

    render() {
      this.updateHeroExplorePosition();
      if (this.notifyPopupOpen) return;
      const galleryRect = this.gallery?.getBoundingClientRect();
      if (galleryRect && this.track && window.innerWidth >= 991) {
        const range = this.galleryScrollRange || Math.max(1, this.travel);
        const progress = Math.max(0, Math.min(1, ((this.headerHeight || 0) - galleryRect.top) / range));
        this.track.style.transform = `translate3d(${-this.travel * progress}px, 0, 0)`;
      }
    }

    updateHeroExplorePosition() {
      if (!this.hero) return;
      const heroRect = this.hero.getBoundingClientRect();
      const shouldFixToViewport = heroRect.top < window.innerHeight && heroRect.bottom >= window.innerHeight;
      this.classList.toggle('is-hero-explore-fixed', shouldFixToViewport);
    }

    renderHero(progress, colorProgress) {
      const desktopFive = [
        [[23.4, 18.9, 53.1], [32.3, -7, 30.2]],
        [[-7.2, -13.9, 35.4], [-2.7, 26.6, 30.2]],
        [[70.8, -13.9, 35.4], [71.4, -.2, 30.2]],
        [[-7.2, 73.1, 35.4], [13.4, 78.5, 30.2]],
        [[70.8, 73.1, 35.4], [66.7, 68.6, 30.2]]
      ];
      const mobileFive = [
        [[18.5, 269, 64, 0], [18.5, 269, 64, 0]],
        [[0, 119, 50, 0], [0, -37, 52, 0]],
        [[50, 119, 50, 0], [54, -37, 46, 0]],
        [[-3, 428, 47, 0], [0, 428, 45, 0]],
        [[55, 428, 47, 7], [53, 422, 47, 7]]
      ];
      const desktopFour = [
        [[350, 150, 600, 0], [28, 142, 600, 0]],
        [[970, 150, 600, 0], [1219, 4, 600, 0]],
        [[350, 508, 600, 0], [229, 690, 600, 0]],
        [[970, 508, 600, 0], [1280, 618, 600, 0]]
      ];
      const mobileFour = [
        [[-13.3, 119, 64, 0], [-12.8, 32, 64, 0]],
        [[51.7, 119, 64, 0], [55.6, 60, 64, 0]],
        [[27, 422, 68, 7], [52.3, 483, 64, 7]],
        [[7, 269, 64, 0], [-14.1, 411, 64, 0]]
      ];
      const isMobile = window.innerWidth < 991;
      const usesMobileResponsiveY = isMobile;
      const isFourCardDesktop = !isMobile && this.cards.length === 4;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const desktopFourScale = isFourCardDesktop ? viewportWidth / 1920 : 1;
      const desktopFourEase = 1 - Math.pow(1 - progress, 5);
      const desktopFourHeaderOffset = isFourCardDesktop
        ? Math.min(this.headerHeight || this.getHeaderHeight(), 64 * desktopFourScale) * (1 - desktopFourEase)
        : 0;
      const desktopFourCanvasOffset = isFourCardDesktop
        ? Math.max(0, (viewportHeight - 930 * desktopFourScale) / 2)
        : 0;
      const layout = isMobile
        ? (this.cards.length === 4 ? mobileFour : mobileFive)
        : (isFourCardDesktop ? desktopFour : desktopFive);
      const ease = isFourCardDesktop
        ? desktopFourEase
        : this.cards.length === 4
          ? progress
          : 1 - Math.pow(1 - progress, 3);
      this.cards.forEach((card, index) => {
        const points = layout[index % layout.length];
        const current = points[0].map((start, i) => start + (points[1][i] - start) * ease);
        if (isFourCardDesktop) {
          card.style.setProperty('--vrd-card-x', `${current[0] * desktopFourScale}px`);
          card.style.setProperty('--vrd-card-y', `${current[1] * desktopFourScale + desktopFourCanvasOffset - desktopFourHeaderOffset}px`);
          card.style.setProperty('--vrd-card-width', `${current[2] * desktopFourScale}px`);
        } else {
          card.style.setProperty('--vrd-card-x', `${current[0]}vw`);
          card.style.setProperty('--vrd-card-y', `${usesMobileResponsiveY ? current[1] / 3.75 : current[1]}${usesMobileResponsiveY ? 'vw' : 'vh'}`);
          card.style.setProperty('--vrd-card-width', `${current[2]}vw`);
        }
        card.style.setProperty('--r', `${current[3] || 0}deg`);
      });
      if (this.heading) {
        this.heading.style.opacity = String(progress);
        this.heading.style.pointerEvents = progress > .08 ? 'auto' : 'none';
      }
      if (this.headingAccent) {
        const colorEase = colorProgress * colorProgress * (3 - 2 * colorProgress);
        const start = [23, 23, 23];
        const end = [70, 105, 26];
        const color = start.map((channel, index) => Math.round(channel + (end[index] - channel) * colorEase));
        this.headingAccent.style.color = `rgb(${color.join(', ')})`;
      }
    }

    goToSlide(index, smooth = true) {
      const slide = this.slides[index];
      if (!slide || !this.gallery || !this.track) return;
      if (window.innerWidth < 991) {
        this.setMobileSlide(index);
        const galleryTop = this.gallery.getBoundingClientRect().top + window.scrollY;
        const stickyHeaderHeight = this.getStickyHeaderHeight();
        window.scrollTo({ top: galleryTop - stickyHeaderHeight, behavior: smooth && !this.reducedMotion ? 'smooth' : 'auto' });
        return;
      }
      this.measure();
      const maxHorizontal = Math.max(1, this.track.scrollWidth - window.innerWidth);
      const horizontal = Math.min(slide.offsetLeft, maxHorizontal);
      const scrollRange = this.galleryScrollRange || Math.max(0, this.travel);
      const galleryTop = this.gallery.getBoundingClientRect().top + window.scrollY;
      const stickyTop = this.headerHeight || 0;
      window.scrollTo({ top: galleryTop - stickyTop + (horizontal / maxHorizontal) * scrollRange, behavior: smooth && !this.reducedMotion ? 'smooth' : 'auto' });
    }

    setMobileSlide(index, animate = true) {
      if (!this.slides.length) return;
      const previousIndex = this.mobileActiveIndex;
      const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const direction = nextIndex === previousIndex ? 0 : nextIndex > previousIndex ? 1 : -1;
      const previousSlide = this.slides[previousIndex];
      const nextSlide = this.slides[nextIndex];
      const shouldAnimateMedia = animate && !this.reducedMotion && window.innerWidth < 991 && direction !== 0;
      const copyAnimationClasses = [
        'is-mobile-copy-enter-next',
        'is-mobile-copy-exit-next',
        'is-mobile-copy-enter-prev',
        'is-mobile-copy-exit-prev'
      ];

      window.clearTimeout(this.mobileTransitionTimer);
      this.slides.forEach((slide) => slide.classList.remove('is-mobile-outgoing', 'is-mobile-media-no-transition', ...copyAnimationClasses));

      if (shouldAnimateMedia) {
        previousSlide?.classList.add('is-mobile-outgoing');
        previousSlide?.classList.add(direction > 0 ? 'is-mobile-copy-exit-next' : 'is-mobile-copy-exit-prev');
        nextSlide?.classList.add(direction > 0 ? 'is-mobile-copy-enter-next' : 'is-mobile-copy-enter-prev');
        previousSlide?.style.setProperty('--vrd-mobile-media-x', '0%');
        nextSlide?.classList.add('is-mobile-media-no-transition');
        nextSlide?.style.setProperty('--vrd-mobile-media-x', `${direction * 100}%`);
        void nextSlide?.offsetWidth;
      }

      this.mobileActiveIndex = nextIndex;
      this.querySelectorAll('[data-mobile-prev]').forEach((button) => {
        button.disabled = nextIndex === 0;
      });
      this.querySelectorAll('[data-mobile-next]').forEach((button) => {
        button.disabled = nextIndex === this.slides.length - 1;
      });
      const progressWidth = 68 / this.slides.length;
      this.style.setProperty('--vrd-mobile-progress-left', `${16 + progressWidth * nextIndex}%`);
      this.style.setProperty('--vrd-mobile-progress-width', `${progressWidth}%`);
      this.classList.toggle('is-mobile-switching', animate);
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('is-mobile-active', active);
        if (window.innerWidth < 991) {
          slide.setAttribute('aria-hidden', String(!active));
          slide.inert = !active;
        }
        if (!active) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
          slide.querySelectorAll('.dl-vrd__media.is-playing').forEach((media) => media.classList.remove('is-playing'));
        } else if (window.innerWidth < 991 && animate) {
          slide.querySelectorAll('video[data-vrd-autoplay="true"]').forEach((video) => {
            if (video.dataset.vrdAutoplayDone === 'true') return;
            video.dataset.vrdAutoplayDone = 'true';
            video.controls = false;
            this.videoObserver?.unobserve(video.closest('.dl-vrd__media'));
            this.prepareVideo(video);
            video.play().catch(() => {});
          });
        }
      });
      this.syncMobileQaHeight(nextIndex);

      if (window.innerWidth < 991) {
        if (shouldAnimateMedia) {
          nextSlide?.classList.remove('is-mobile-media-no-transition');
          previousSlide?.style.setProperty('--vrd-mobile-media-x', `${direction * -100}%`);
          nextSlide?.style.setProperty('--vrd-mobile-media-x', '0%');
          this.mobileTransitionTimer = window.setTimeout(() => {
            previousSlide?.classList.remove('is-mobile-outgoing');
            this.slides.forEach((slide) => slide.classList.remove(...copyAnimationClasses));
            this.positionMobileMedia(nextIndex);
          }, 440);
        } else {
          this.positionMobileMedia(nextIndex);
        }
      }
    }

    syncMobileQaHeight(index) {
      if (window.innerWidth >= 991) return;
      const qa = this.slides[index]?.querySelector('.dl-vrd__qa');
      if (!qa) return;
      const nextHeight = Math.max(1, Math.ceil(qa.getBoundingClientRect().height));
      if (nextHeight === this.mobileQaHeight) return;
      this.mobileQaHeight = nextHeight;
      this.style.setProperty('--vrd-mobile-qa-height', `${nextHeight}px`);
    }

    positionMobileMedia(activeIndex) {
      this.slides.forEach((slide, slideIndex) => {
        slide.style.setProperty('--vrd-mobile-media-x', slideIndex === activeIndex ? '0%' : slideIndex < activeIndex ? '-100%' : '100%');
      });
    }

    async copyDiscountCode(button) {
      const code = button.dataset.copyCode;
      if (!code) return;

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const input = document.createElement('textarea');
          input.value = code;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }

        window.clearTimeout(button.copyCodeTimer);
        button.classList.add('is-copied');
        const copyLabel = button.dataset.copyLabel || 'Copy Code';
        const copiedLabel = button.dataset.copiedLabel || 'Copied';
        const codeLabel = button.dataset.copyCode || '';
        button.setAttribute('aria-label', `${copiedLabel} ${codeLabel}`.trim());
        button.copyCodeTimer = window.setTimeout(() => {
          button.classList.remove('is-copied');
          button.setAttribute('aria-label', `${copyLabel} ${codeLabel}`.trim());
        }, 1600);
      } catch (error) {
        button.classList.remove('is-copied');
      }
    }
  }

  customElements.define('dl-page-visual-roll-difference', DlPageVisualRollDifference);
})();
